/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useState } from 'react';
import Catch from 'donor/utils/Catch';
import { SPACERS } from 'shared/design-system/settings/variables/Spacers';
import styled from 'styled-components';
import { COLORS } from 'shared/design-system/settings/variables/Colors';
import TrueImpactButton from 'shared/design-system/components/TrueImpactButton';
import TrueImpactText from 'shared/design-system/base/TrueImpactText';
import TrueImpactTextfield from 'shared/design-system/components/TrueImpactTextfield';
import { useUserActions } from 'shared/auth/hooks/useUserActions';
// import { notifyErrorToSlackRequest } from './api';
import { useApiRequest } from 'shared/auth/hooks/useApiRequest';
import { sendSlackMessage, SlackMessagePayload } from 'shared/auth/ui/utils';

const refreshPage = () => {
  window.location.reload();
};

const getFormattedMessage = ({
  stack,
  message,
  email,
  type,
  url,
}: {
  stack: string;
  message?: string;
  email: string;
  type: string;
  url: string;
}): SlackMessagePayload => {
  return {
    payload: {
      text: 'Something went wrong',
      blocks: [
        {
          type: 'section',
          text: {
            type: 'mrkdwn',
            text: `*User:* ${email}\n*User Type:* ${type}\n*URL:* <${url}|${url}>\n*Message from user:* ${message ||
              'No message provided.'}`,
          },
        },
        {
          type: 'divider',
        },
        {
          type: 'section',
          text: {
            type: 'mrkdwn',
            text: stack,
          },
        },
      ],
    },
  };
};

const StyledDiv = styled.div`
  max-width: 600px;
  height: fit-content;
  margin: ${SPACERS.large} auto;
  border-radius: ${SPACERS.tiny};
  border: 1px solid ${COLORS.ctaPrimary};
  padding: ${SPACERS.extraLarge};
  .refresh-button {
    margin-top: ${SPACERS.medium};
    margin-bottom: ${SPACERS.medium};
  }
  textarea {
    min-height: 120px;
  }
  .notify-button {
    margin-top: ${SPACERS.medium};
    margin-left: auto;
    margin-right: auto;
  }
`;

const formatErrorStack = (error: any, componentStack?: string) => {
  if (!error || !error.stack) {
    return `*Error:* ${error?.message ||
      'No error message available'}\n\n*Stack Trace:* No stack trace available.`;
  }
  const stackLines = error.stack.split('\n');
  const formattedStack = stackLines.map((line: any) => line.trim()).join('\n');

  const formattedComponentStack = componentStack
    ? `\n\n*Component Stack:*\n${componentStack.trim()}`
    : '';

  return `*Error:* ${error.message}\n\n*Stack Trace:*\n${formattedStack}${formattedComponentStack}`;
};

const SubmitButton: React.FC<{
  error: any;
  componentStack?: string;
  className?: string;
  onSuccess: () => unknown;
  getMessage: () => string | undefined;
}> = ({ error, componentStack, className, onSuccess, getMessage }) => {
  const { email, isDonor, isNPO } = useUserActions();
  const [requestState, makeRequest] = useApiRequest<{
    success: boolean;
    data?: any;
  }>();

  const handleSubmit = async () => {
    const stack = formatErrorStack(error, componentStack);
    const message = getMessage();
    const url = window.location.href;
    const type = isNPO ? 'NPO' : isDonor ? 'Donor' : 'Admin or Unknown';
    const body = getFormattedMessage({ stack, message, email, type, url });
    const request = sendSlackMessage(body);
    makeRequest(request);
    request.then(res => {
      if (res.success) {
        onSuccess();
      }
    });
  };

  return (
    <>
      {requestState.type === 'REQUEST_ERROR' && (
        <TrueImpactText color="critical">
          {requestState.error.message}
        </TrueImpactText>
      )}
      {requestState.type === 'REQUEST_SUCCESS' && (
        <TrueImpactText color="success">
          Successfully notified True Impact. You can refresh your page now.
        </TrueImpactText>
      )}
      <TrueImpactButton
        onClick={handleSubmit}
        className={className}
        isLoading={requestState.type === 'REQUEST_START'}
      >
        Notify True Impact
      </TrueImpactButton>
    </>
  );
};

const isChunkLoadFailed = (message?: string): boolean => {
  if (!message) return false; // Return false if the message is undefined or empty

  const regex = /Loading .* chunk .* failed/i; // Regular expression to match the error pattern
  return regex.test(message) || message.includes('Loading chunk'); // Test the message against the regex
};

export const RenderErrorBoundary: React.FC<{
  error?: any;
  message?: string;
  componentStack?: string;
}> = ({ error, message, componentStack }) => {
  const [success, setSuccess] = useState<boolean>(false);
  const isChunkError = isChunkLoadFailed(message);
  return (
    <StyledDiv>
      <TrueImpactText variant="heading">
        {isChunkError ? 'We\'ve updated the site!' : 'Something went wrong'}
      </TrueImpactText>
      <TrueImpactText>Please try refreshing the page.</TrueImpactText>
      <TrueImpactButton
        variant="secondary-large"
        onClick={refreshPage}
        className="refresh-button"
      >
        Refresh now
      </TrueImpactButton>
      {success && (
        <TrueImpactText color="success">
          Successfully notified True Impact. You can refresh your page now.
        </TrueImpactText>
      )}
      {!isChunkError && !success && (
        <>
          <TrueImpactText>
            If the error persists, please notify us by clicking the button
            below.
          </TrueImpactText>
          <TrueImpactTextfield
            id="notify-textfield"
            isTextarea={true}
            placeholder="Message (optional)"
          />
          <SubmitButton
            error={error}
            componentStack={componentStack}
            onSuccess={() => setSuccess(true)}
            getMessage={() =>
              (document.getElementById(
                'notify-textfield',
              ) as HTMLTextAreaElement)?.value || ''
            }
            className="notify-button"
          />
        </>
      )}
    </StyledDiv>
  );
};

const ErrorBoundary = Catch((props: any, error: any, errorInfo?: any) => {
  if (error) {
    return (
      <RenderErrorBoundary
        error={error}
        message={error?.message}
        componentStack={errorInfo?.componentStack}
      />
    );
  }
  return props.children;
});

export default ErrorBoundary;
